/**
 * The training course's props (ROUND7 §5): the practice wall in short pieces, the range's lamps and crates,
 * the firing line, and a fringe of trees down both edges so the strip reads as ground rather than a board.
 * Everything uses the real prop kinds, so the sim, the renderer and the validator treat it as they treat
 * any other mission. Pure.
 */

import { WALLS } from '../../sim/tuningWalls';
import type { PropDef, PropKind } from '../../sim/types';
import { COURSE, COURSE_IDS } from './layout';

function prop(id: number, kind: PropKind, x: number, z: number, extra: Partial<PropDef> = {}): PropDef {
  return { id, kind, x, z, yaw: 0, ...extra };
}

/**
 * The wall, west to east: short pieces either side of the marked middle section (ROUND7 §4 lays every wall in
 * pieces no longer than `WALLS.pieceMax`, so a breach opens a gap and not the whole face). The middle section
 * is the objective's target and a little longer, so the gap is where the trainee is told to make it.
 */
export function wallPieces(): PropDef[] {
  const w = COURSE.wall;
  const piece = (id: number, [a, b]: [number, number], target = false): PropDef =>
    prop(id, 'wall', (a + b) / 2, w.z, target ? { length: b - a, target: true } : { length: b - a });
  return [
    ...splitRun(w.x0, w.targetX0).map((span, i) => piece(COURSE_IDS.wallWest + i, span)),
    piece(COURSE_IDS.wallTarget, [w.targetX0, w.targetX1], true),
    ...splitRun(w.targetX1, w.x1).map((span, i) => piece(COURSE_IDS.wallEast + i, span)),
  ];
}

/** `[a, b]` in equal pieces no longer than `WALLS.pieceMax`. */
function splitRun(a: number, b: number): Array<[number, number]> {
  const count = Math.max(1, Math.ceil((b - a) / WALLS.pieceMax - 1e-9));
  const step = (b - a) / count;
  const out: Array<[number, number]> = [];
  for (let i = 0; i < count; i++) out.push([a + step * i, a + step * (i + 1)]);
  return out;
}

function rangeProps(): PropDef[] {
  const r = COURSE.range;
  const out: PropDef[] = [];
  r.lamps.forEach((p, i) => out.push(prop(COURSE_IDS.lamps[i] ?? 230 + i, 'searchlight', p.x, p.z)));
  r.crates.forEach((p, i) => out.push(prop(COURSE_IDS.crates[i] ?? 240 + i, 'crate', p.x, p.z)));
  out.push(prop(COURSE_IDS.sandbags, 'sandbags', r.sandbags.x, r.sandbags.z, { length: r.sandbags.length }));
  return out;
}

/** Nothing of the fringe stands this near the stream's water, metres: a tree in a stream is a prop in water. */
const STREAM_CLEAR = 3;

/**
 * Trees and rocks down the east and west edges, north of the drop band only: the band stays bare so no landing
 * is a rough one, and the lane stays bare so no pod comes down on anything. Laid by rule, not by dice, so the
 * course is the same for everyone.
 */
function fringe(): PropDef[] {
  const out: PropDef[] = [];
  const kinds: PropKind[] = ['pine', 'broadleaf', 'pine', 'rock', 'broadleaf', 'shrub'];
  const stream = COURSE.dogs.stream;
  let id = 300;
  let n = 0;
  for (let z = 22; z < COURSE.dropBand.z0 - 10; z += 17) {
    for (const x of [4.5, COURSE.sizeX - 4.5]) {
      const kind = kinds[n % kinds.length] ?? 'pine';
      // A small stagger so the rows do not read as a fence.
      const jitter = (n % 3) - 1;
      const at = z + (n % 2) * 4;
      n++;
      if (at > stream.north - STREAM_CLEAR && at < stream.south + STREAM_CLEAR) continue;
      out.push(prop(id++, kind, x + jitter, at));
    }
  }
  return out;
}

export function courseProps(): PropDef[] {
  return [...wallPieces(), ...rangeProps(), ...fringe()];
}
